import { Capacitor } from '@capacitor/core'
import { useMemo } from 'react'
import { useRecoilValue, useResetRecoilState } from 'recoil'
import { useTheme } from 'styled-components'

import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { environment } from '@/config/environment'
import { useAppInfo } from '@/modules/shared/hooks/useAppInfo'
import { mutedBannerColor } from '@/utils/color'
import { DEV_APP_ID, STAGING_APP_ID } from '@/utils/environment'
import { modeAtom, useQaClick } from '@/utils/qa-mode'
import { BannerContainer, StyledVersionButton } from './EnvironmentBanner.styled'

export const shouldShowBanner = () => {
  return !(environment.env === 'production' || window.location.pathname.includes('content-preview'))
}

const EnvironmentBanner = () => {
  const theme = useTheme()
  const info = useAppInfo()
  const appId = info?.id

  const isQaMode = useRecoilValue(modeAtom) === 'qa'
  const resetMode = useResetRecoilState(modeAtom)
  const handleVersionClick = useQaClick()

  const color = useMemo(() => {
    const muted = (bannerColor: string) => mutedBannerColor(bannerColor, theme.Colors.Background.Light)

    if (Capacitor.isNativePlatform()) {
      if (appId === DEV_APP_ID) {
        return muted(theme.Colors.Status.Error)
      }

      if (appId === STAGING_APP_ID) {
        return muted(theme.Colors.Status.Warning)
      }
    } else {
      if (environment.env === 'development') {
        return muted(theme.Colors.Status.Error)
      }

      if (environment.env === 'staging') {
        return muted(theme.Colors.Status.Warning)
      }
    }
    if (isQaMode) return muted(theme.Colors.Status.Success)
  }, [theme, appId, isQaMode])

  const textColor = useMemo(() => {
    if (Capacitor.isNativePlatform()) {
      if (appId === DEV_APP_ID) {
        return theme.Colors.Neutrals.Lightest
      }
      if (appId === STAGING_APP_ID) {
        return theme.Colors.Neutrals.Darkest
      }
    } else {
      if (environment.env === 'development') {
        return theme.Colors.Neutrals.Lightest
      }
      if (environment.env === 'staging') {
        return theme.Colors.Neutrals.Darkest
      }
    }
    if (isQaMode) return theme.Colors.Neutrals.Darkest
  }, [theme, appId, isQaMode])

  // Show banner if QA mode is active, even in production
  if ((environment.env === 'production' && !isQaMode) || !info || !appId) {
    return null
  }

  // Hide specifically for preview routes
  if (window.location.pathname.includes('content-preview')) {
    return null
  }
  return (
    <BannerContainer color={color}>
      <StyledVersionButton type='button' onClick={handleVersionClick} $textColor={textColor}>
        <Span color={textColor} type='BodySmallRegular'>
          v{APP_VERSION}
          {info.build && ` (${info.build})`}
        </Span>
      </StyledVersionButton>
      {isQaMode && (
        <Button variant='Tertiary' size='small' inline onClick={resetMode}>
          Tester mode afsluiten
        </Button>
      )}
    </BannerContainer>
  )
}

export { EnvironmentBanner }
